import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaChevronDown, FaChevronRight, FaPlus, FaWarehouse, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 20;

const CmaList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchData = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/cma-get?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  // Group data by CMA
  const groupedData = data.reduce((acc, row) => {
    if (!acc[row.cma_id]) {
      acc[row.cma_id] = {
        cma_id: row.cma_id,
        cma_name: row.cma_name,
        type: row.cma_type,
        contact_person: row.contact_person,
        phone: row.phone,
        email: row.email,
        address: row.address,
        gst_no: row.gst_no,
        cma_state: row.cma_state,
        state_code: row.state_code,
        account_no: row.account_no,
        ifsc_code: row.ifsc_code,
        bank_name: row.bank_name,
        bank_address: row.bank_address,
        warehouses: []
      };
    }
    if (row.warehouse_id) {
      acc[row.cma_id].warehouses.push({
        warehouse_id: row.warehouse_id,
        warehouse_name: row.warehouse_name,
        type: row.warehouse_type,
        location: row.warehouse_location,
        state: row.warehouse_state
      });
    }
    return acc;
  }, {});
  const cmaList = Object.values(groupedData);

  // Remove client-side filtering and pagination
  // Filtered data based on search (search on CMA fields only)
  // Pagination
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const handleExpand = (cma_id) => {
    setExpanded(prev => ({ ...prev, [cma_id]: !prev[cma_id] }));
  };

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">CMA / Transporter & Warehouse </h2>
        <div className="flex gap-1 w-full sm:w-auto justify-end">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search records..."
              value={search}
              onChange={handleSearchChange}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-56 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
            onClick={() => navigate('/cma/new')}
          >
            <FaPlus className="text-[10px]" />
            Add New
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-[13px]">{error}</div>
      ) : cmaList.length === 0 ? (
        <NoRecordFound message="No CMA records found." />
      ) : (
        <div className="overflow-x-auto border border-gray-300 rounded-sm">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-[#f4f4f4] border-b border-gray-300">
              <tr>
                <th className="px-2 py-1.5"></th>
               
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">CMA Name</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Type</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Contact Person</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Phone</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Email</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">GST No</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">State</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">State Code</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Address</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Account No</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">IFSC Code</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Bank Name</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Bank Address</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {cmaList.map((row, idx) => (
                <React.Fragment key={row.cma_id}>
                  <tr className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                    <td className="px-2 py-1 text-center cursor-pointer" onClick={() => handleExpand(row.cma_id)}>
                      {expanded[row.cma_id] ? <FaChevronDown /> : <FaChevronRight />}
                    </td>
                   
                    <td className="px-3 py-1.5 text-gray-900 font-semibold text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.cma_name}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.type}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.contact_person}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.phone}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.email}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.gst_no}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.cma_state}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.state_code}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.address}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.account_no}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.ifsc_code}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.bank_name}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{row.bank_address}</td>
                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">
                      <div className="flex gap-1">
                        <button
                          onClick={() => navigate(`/cma/edit/${row.cma_id}`)}
                          className="text-[12px] px-2 py-1 bg-[#15803d] text-white rounded hover:bg-[#166534] border border-[#15803d] flex items-center gap-1"
                          title="Edit CMA"
                        >
                          <FaEdit />
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/cma/${row.cma_id}/add-warehouses`)}
                          className="text-[12px] px-2 py-1 bg-[#15803d] text-white rounded hover:bg-[#166534] border border-[#15803d] flex items-center gap-1"
                          title="Add Warehouses"
                        >
                          <FaWarehouse />
                          Add WH
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded[row.cma_id] && (
                    <tr>
                      <td></td>
                      <td colSpan={5} className="p-0">
                        <div className="p-2">
                          {row.warehouses.length === 0 ? (
                            <div className="text-gray-400 text-[12px]">No warehouses.</div>
                          ) : (
                            <table className="min-w-full text-[13px] border">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Warehouse ID</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Warehouse Name</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Type</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Location</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">State</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {row.warehouses.map((wh) => (
                                  <tr key={wh.warehouse_id}>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.warehouse_id}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.warehouse_name}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.type}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.location}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.state}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-[12px]">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#15803d] border-[#15803d] hover:text-white hover:bg-[#166534]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#15803d] border-[#15803d] hover:text-white hover:bg-[#166534]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CmaList;